import type { Bedingung, Einzelbedingung } from "./schema";

/**
 * Auswertung der Bedingungen.
 *
 * Läuft im Browser bei jedem Schrittwechsel und auf dem Server beim Prüfen
 * eines eingehenden Leads. Beide müssen zum selben Ergebnis kommen, sonst
 * entstehen Leads mit Antworten auf Fragen, die der Nutzer nie gesehen hat.
 * Deshalb genau eine Umsetzung, geteilt von beiden Seiten.
 */

export type Antworten = Record<string, unknown>;

function istEinzelbedingung(b: Bedingung): b is Einzelbedingung {
  return "step" in b;
}

export function bedingungErfuellt(bedingung: Bedingung, antworten: Antworten): boolean {
  if ("all" in bedingung) {
    return bedingung.all.every((teil) => bedingungErfuellt(teil, antworten));
  }
  if ("any" in bedingung) {
    return bedingung.any.some((teil) => bedingungErfuellt(teil, antworten));
  }
  if ("not" in bedingung) {
    return !bedingungErfuellt(bedingung.not, antworten);
  }
  if (istEinzelbedingung(bedingung)) {
    return einzelPruefen(bedingung, antworten[bedingung.step]);
  }
  return false;
}

function alsListe(wert: unknown): unknown[] {
  if (Array.isArray(wert)) return wert;
  if (wert === null || wert === undefined) return [];
  return [wert];
}

function alsZahl(wert: unknown): number | null {
  if (typeof wert === "number") return Number.isFinite(wert) ? wert : null;
  if (typeof wert === "string" && wert.trim() !== "") {
    // Datumsangaben lassen sich als Zeitstempel vergleichen. Damit funktionieren
    // gt und lt auch für Termine, ohne einen eigenen Operator dafür.
    if (/^\d{4}-\d{2}(-\d{2})?$/.test(wert)) {
      const zeit = new Date(wert).getTime();
      return Number.isNaN(zeit) ? null : zeit;
    }
    const zahl = Number(wert);
    return Number.isFinite(zahl) ? zahl : null;
  }
  return null;
}

function istLeer(wert: unknown): boolean {
  if (wert === null || wert === undefined) return true;
  if (typeof wert === "string") return wert.trim() === "";
  if (Array.isArray(wert)) return wert.length === 0;
  return false;
}

function einzelPruefen(bedingung: Einzelbedingung, antwort: unknown): boolean {
  const erwartet = bedingung.value;

  switch (bedingung.op) {
    case "is_empty":
      return istLeer(antwort);
    case "is_not_empty":
      return !istLeer(antwort);

    case "eq":
      return gleich(antwort, erwartet);
    case "neq":
      return !gleich(antwort, erwartet);

    case "in":
      return alsListe(erwartet).some((e) => gleich(antwort, e));
    case "not_in":
      return !alsListe(erwartet).some((e) => gleich(antwort, e));

    // Für Mehrfachauswahl: Enthält die Antwort diesen Wert?
    case "contains":
      return alsListe(antwort).some((a) => gleich(a, erwartet));
    case "not_contains":
      return !alsListe(antwort).some((a) => gleich(a, erwartet));

    case "gt":
    case "gte":
    case "lt":
    case "lte": {
      const a = alsZahl(antwort);
      const b = alsZahl(erwartet);
      // Ein Vergleich mit einer fehlenden oder unvergleichbaren Angabe ist
      // nicht wahr. Er ist aber auch nicht falsch im Sinne von "das Gegenteil
      // trifft zu" - deshalb liefern gt und lte hier beide false.
      if (a === null || b === null) return false;
      if (bedingung.op === "gt") return a > b;
      if (bedingung.op === "gte") return a >= b;
      if (bedingung.op === "lt") return a < b;
      return a <= b;
    }

    case "matches": {
      if (typeof antwort !== "string" || typeof erwartet !== "string") return false;
      /**
       * Bewusst kein regulärer Ausdruck.
       *
       * Ein Muster aus dem Funnel-Dokument wäre eine Zeichenkette, die von
       * außen kommt. Ein regulärer Ausdruck daraus lässt sich so bauen, dass
       * seine Auswertung den Server minutenlang beschäftigt. Für den
       * eigentlichen Zweck - "Postleitzahl beginnt mit 66" - genügt ein
       * Präfixvergleich mit optionalem Stern.
       */
      const muster = erwartet.endsWith("*") ? erwartet.slice(0, -1) : erwartet;
      return erwartet.endsWith("*") ? antwort.startsWith(muster) : antwort === muster;
    }

    default:
      return false;
  }
}

function gleich(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  // Zahlen aus Formularfeldern kommen als Zeichenkette an. "3" und 3 sollen
  // gleich sein, "" und 0 nicht.
  if (typeof a === "number" && typeof b === "string" && b.trim() !== "") {
    return a === Number(b);
  }
  if (typeof a === "string" && typeof b === "number" && a.trim() !== "") {
    return Number(a) === b;
  }
  return false;
}
